import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translate, SUPPORTED_LANGS } from "@/i18n/translations";
import { api } from "@/lib/api";

const I18nContext = createContext(null);
const STORAGE_KEY = "nylsu.lang";
const EXPLICIT_KEY = "nylsu.lang.explicit";

function detectInitial() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGS.some((l) => l.code === saved)) return saved;
  } catch {}
  const nav = (typeof navigator !== "undefined" && navigator.language) || "en";
  if (nav.toLowerCase().startsWith("tr")) return "tr";
  return "en";
}

function readExplicit() {
  try { return localStorage.getItem(EXPLICIT_KEY) === "1"; } catch { return false; }
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(detectInitial);
  const [explicit, setExplicit] = useState(readExplicit);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  // Sync from user profile on mount / when user changes — only if the user
  // has NOT explicitly chosen a language on this device.
  const syncFromUser = useCallback((user) => {
    if (!user || !user.language) return;
    if (readExplicit()) return; // respect the user's manual choice
    if (SUPPORTED_LANGS.some((l) => l.code === user.language)) {
      setLang(user.language);
    }
  }, []);

  const changeLang = useCallback(async (next) => {
    if (!SUPPORTED_LANGS.some((l) => l.code === next)) return;
    setLang(next);
    setExplicit(true);
    try { localStorage.setItem(EXPLICIT_KEY, "1"); } catch {}
    try { await api.patch("/auth/me", { language: next }); } catch {}
  }, []);

  const t = useCallback((key, params) => translate(lang, key, params), [lang]);
  const tEnum = useCallback((scope, value) => (value == null ? "" : translate(lang, `${scope}.${value}`, value)), [lang]);
  const localName = useCallback((obj, field = "name") => {
    if (!obj) return "";
    const trKey = `${field}_tr`;
    if (lang === "tr" && obj[trKey]) return obj[trKey];
    return obj[field] || "";
  }, [lang]);
  const fmtNumber = useCallback((v, opts = {}) => new Intl.NumberFormat(lang === "tr" ? "tr-TR" : "en-GB", opts).format(v ?? 0), [lang]);
  const fmtCurrency = useCallback((v, currency = "EUR") => new Intl.NumberFormat(lang === "tr" ? "tr-TR" : "en-GB", { style: "currency", currency, maximumFractionDigits: 0 }).format(v ?? 0), [lang]);
  const fmtDateTime = useCallback((iso) => (iso ? new Date(iso).toLocaleString(lang === "tr" ? "tr-TR" : "en-GB") : ""), [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang: changeLang, syncFromUser, t, tEnum, localName, fmtNumber, fmtCurrency, fmtDateTime, langs: SUPPORTED_LANGS, explicit }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
